import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HelpCircle, Plus, MessageSquare, Send, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tickets');
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'general' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tRes, fRes] = await Promise.all([
        api.get('/api/support/tickets'),
        api.get('/api/support/faq')
      ]);
      setTickets(tRes.data.tickets || []);
      setFaq(fRes.data.faq || []);
    } catch {} finally { setLoading(false); }
  };

  const createTicket = async () => {
    try { await api.post('/api/support/tickets', ticketForm); toast.success('Ticket created'); setShowTicketDialog(false); loadData(); }
    catch { toast.error('Failed'); }
  };

  const createFaq = async () => {
    try { await api.post('/api/support/faq', faqForm); toast.success('FAQ added'); setShowFaqDialog(false); loadData(); }
    catch { toast.error('Failed'); }
  };

  const respondToTicket = async () => {
    if (!selectedTicket || !response.trim()) return;
    try { await api.put(`/api/support/tickets/${selectedTicket.id}`, { response, status: 'in_progress' }); toast.success('Response sent'); setResponse(''); loadData(); setSelectedTicket(tickets.find(t => t.id === selectedTicket.id)); }
    catch { toast.error('Failed'); }
  };

  const STATUS_C = { open: '#B3261E', in_progress: '#9A6B1A', resolved: '#1F7A5A', closed: '#5B616E' };

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold flex items-center gap-2"><HelpCircle className="w-6 h-6 text-[#B89B5E]" /> Support Center</h1><p className="text-sm text-[#5B616E] mt-1">Manage tickets and knowledge base</p></div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="tickets">Tickets</TabsTrigger><TabsTrigger value="faq">FAQ / Knowledge Base</TabsTrigger></TabsList>
        <TabsContent value="tickets" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button onClick={() => setShowTicketDialog(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Ticket</Button></div>
          {loading ? <Skeleton className="h-48" /> : tickets.length > 0 ? (
            <div className="space-y-3">
              {tickets.map(t => (
                <Card key={t.id} className="bg-white border-[#E7E0D6] cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedTicket(t)}>
                  <CardContent className="pt-4 flex items-center gap-4">
                    <div className="flex-1"><p className="font-medium text-sm">{t.subject}</p><p className="text-xs text-[#5B616E] mt-0.5">{t.description?.slice(0, 100)}</p></div>
                    <Badge className="text-[10px]" style={{ background: `${STATUS_C[t.status]||'#5B616E'}15`, color: STATUS_C[t.status]||'#5B616E' }}>{t.status}</Badge>
                    <span className="text-xs text-[#5B616E]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : <div className="text-center py-12 text-[#5B616E]">No tickets</div>}
        </TabsContent>
        <TabsContent value="faq" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button onClick={() => setShowFaqDialog(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> Add FAQ</Button></div>
          {faq.length > 0 ? faq.map(f => (
            <Card key={f.id} className="bg-white border-[#E7E0D6]"><CardContent className="pt-4"><p className="font-medium text-sm">{f.question}</p><p className="text-xs text-[#5B616E] mt-2">{f.answer}</p></CardContent></Card>
          )) : <div className="text-center py-12 text-[#5B616E]">No FAQ entries</div>}
        </TabsContent>
      </Tabs>

      {/* Ticket Detail */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedTicket?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#5B616E]">{selectedTicket?.description}</p>
            {(selectedTicket?.responses || []).map((r, i) => (<div key={i} className="p-3 bg-[#FBF7EF] rounded-lg text-sm"><p>{r.content}</p><p className="text-[10px] text-[#5B616E] mt-1">{r.created_at}</p></div>))}
            <div className="flex gap-2"><Textarea value={response} onChange={e => setResponse(e.target.value)} placeholder="Reply..." className="flex-1" /><Button onClick={respondToTicket} className="self-end bg-[#121316] text-white"><Send className="w-4 h-4" /></Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent><DialogHeader><DialogTitle>New Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={ticketForm.subject} onChange={e => setTicketForm({...ticketForm, subject: e.target.value})} placeholder="Subject" />
            <Textarea value={ticketForm.description} onChange={e => setTicketForm({...ticketForm, description: e.target.value})} placeholder="Description" />
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowTicketDialog(false)}>Cancel</Button><Button onClick={createTicket} className="bg-[#121316] text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showFaqDialog} onOpenChange={setShowFaqDialog}>
        <DialogContent><DialogHeader><DialogTitle>Add FAQ</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={faqForm.question} onChange={e => setFaqForm({...faqForm, question: e.target.value})} placeholder="Question" />
            <Textarea value={faqForm.answer} onChange={e => setFaqForm({...faqForm, answer: e.target.value})} placeholder="Answer" rows={4} />
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowFaqDialog(false)}>Cancel</Button><Button onClick={createFaq} className="bg-[#121316] text-white">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
